// One recipient's copy of the document, with the content their rules hide removed.
//
// Deliberately a pass OVER the document rather than a condition callback threaded INTO
// `flowRender`. Three reasons, all of them about the renderer rather than about conditions:
// `flatten` derives a list item's marker from its position, so an item has to be gone before it
// is numbered or the list counts 1, 3, 4; `layoutCellBlocks` runs twice per cell, once to
// measure a row's height and once to draw it, so a condition answered inside it could size a row
// for content it does not draw; and `placeTable` builds its rows, its grid and its laid-out
// cells in index lockstep. Resolving first leaves all 800-odd lines of pagination untouched and
// unre-verified, and makes the result the thing every other check should run against — the
// signature slots and unlinked fields THIS recipient will actually receive.
import type {
	DocumentFlowDoc,
	FlowBlock,
	FlowDate,
	FlowListItem,
	FlowSignature,
	FlowTableRow,
} from './schema';
import type { FlowPredicateEvaluator, FlowRules } from './rules';
import { shownFlowRules } from './rules';
import type { FlowRulePreviewDecision } from './ruleScenario';

/** A block referencing a rule the document does not define. */
export class FlowRuleUnknown extends Error {
	constructor(readonly ruleId: string) {
		super(
			`This document has content marked with rule "${ruleId}", but the document has no such rule. It was most likely pasted in from another document.`
		);
		this.name = 'FlowRuleUnknown';
	}
}

/** Decides whether one block survives, given the rules that showed for this recipient. */
type KeepRule = (rule: string | undefined) => boolean;

const ruleOf = (block: FlowBlock): string | undefined => {
	switch (block.type) {
		case 'paragraph':
		case 'bulletList':
		case 'orderedList':
			return block.attrs?.rule;
		case 'table':
		case 'image':
		case 'signature':
			return block.attrs.rule;
		case 'date':
			// A date anchor carries no rule of its own; one under a ruled block is refused at save.
			return undefined;
	}
};

const visibleBlocks = (blocks: FlowBlock[], keep: KeepRule): FlowBlock[] =>
	blocks.flatMap((block): FlowBlock[] => {
		if (!keep(ruleOf(block))) {
			return [];
		}
		if (block.type === 'bulletList' || block.type === 'orderedList') {
			// Items are filtered BEFORE their contents are walked, which is what makes the
			// surviving items number contiguously: the renderer counts what is left.
			const items = visibleListItems(block.content, keep);
			return items.length === 0 ? [] : [{ ...block, content: items }];
		}
		if (block.type === 'table') {
			const rows = visibleTableRows(block.content, keep);
			// A table whose every row is conditional and none survived is not an empty table on
			// the page — it is no table at all.
			return rows.length === 0 ? [] : [{ ...block, content: rows }];
		}
		return [block];
	});

// An item whose every block was hidden is dropped too: it would still take a number of its own,
// and the list would read 1, 3.
const visibleListItems = (items: FlowListItem[], keep: KeepRule): FlowListItem[] =>
	items
		.filter((item) => keep(item.attrs?.rule))
		.flatMap((item) => {
			const content = visibleBlocks(item.content, keep);
			return content.length === 0 && item.content.length > 0 ? [] : [{ ...item, content }];
		});

const visibleTableRows = (rows: FlowTableRow[], keep: KeepRule): FlowTableRow[] =>
	rows
		.filter((row) => keep(row.attrs?.rule))
		.map((row) => ({
			...row,
			content: row.content.map((cell) => ({
				...cell,
				content: visibleBlocks(cell.content, keep),
			})),
		}));

const keepRuleFor = (rules: FlowRules, evaluate: FlowPredicateEvaluator): KeepRule => {
	const shown = shownFlowRules(rules, evaluate);
	return (rule) => {
		if (rule === undefined) {
			return true;
		}
		if (!Object.hasOwn(rules, rule)) {
			throw new FlowRuleUnknown(rule);
		}
		return shown.has(rule);
	};
};

/**
 * The document as one recipient receives it: each rule's content kept only on a clear yes.
 *
 * Throws {@link FlowRuleUnknown} for content whose rule no longer exists rather than guessing —
 * dropping it would silently delete a clause, and nothing downstream could tell that apart from
 * a clause nobody wrote.
 *
 * `rules` and the surviving blocks' `rule` attributes are left on the result on purpose:
 * `flowRender` ignores both, and keeping them is what lets the generation record say which rule
 * each clause rendered under.
 */
export const resolveFlowRules = (
	doc: DocumentFlowDoc,
	evaluate: FlowPredicateEvaluator
): DocumentFlowDoc => {
	const rules = doc.rules ?? {};
	if (Object.keys(rules).length === 0 && flowRuleReferences(doc).size === 0) {
		return doc;
	}
	return { ...doc, content: visibleBlocks(doc.content, keepRuleFor(rules, evaluate)) };
};

/**
 * Every rule id the document's content actually references.
 *
 * Read from the node tree for the same reason `flowFieldNames` is: a rule cannot be missed by a
 * regex or invented by one. Feeds both publish checks — content pointing at a rule that does not
 * exist, and a rule nothing is marked with, which is a rule the author believes is doing
 * something.
 */
export const flowRuleReferences = (doc: DocumentFlowDoc): Set<string> =>
	new Set(flowRuleUsage(doc).keys());

const countRules = (blocks: FlowBlock[], into: Map<string, number>): void => {
	const bump = (rule: string | undefined): void => {
		if (rule !== undefined) {
			into.set(rule, (into.get(rule) ?? 0) + 1);
		}
	};
	for (const block of blocks) {
		bump(ruleOf(block));
		if (block.type === 'bulletList' || block.type === 'orderedList') {
			for (const item of block.content) {
				bump(item.attrs?.rule);
				countRules(item.content, into);
			}
			continue;
		}
		if (block.type === 'table') {
			for (const row of block.content) {
				bump(row.attrs?.rule);
				for (const cell of row.content) {
					countRules(cell.content, into);
				}
			}
		}
	}
};

/**
 * How many blocks each rule marks.
 *
 * Read off the DOCUMENT rather than the live editor, so the panel showing it is driven by state
 * and not by a ref read during render — which reported "nothing is marked with this rule" for
 * every rule on the first paint of a saved document, then silently corrected itself.
 *
 * A count rather than a set, because a rule used in six places is a different thing to edit than
 * one used in one: changing its test changes all six at once, including the ones off screen.
 */
export const flowRuleUsage = (doc: DocumentFlowDoc): Map<string, number> => {
	const counts = new Map<string, number>();
	countRules(doc.content, counts);
	return counts;
};

/** Every anchor `pick` recognises that sits under a rule, however deeply nested. */
const collectConditionalAnchors = <T>(
	blocks: FlowBlock[],
	underRule: boolean,
	pick: (block: FlowBlock) => T | null,
	into: T[]
): void => {
	for (const block of blocks) {
		const conditional = underRule || ruleOf(block) !== undefined;
		const anchor = pick(block);
		if (anchor !== null) {
			if (conditional) {
				into.push(anchor);
			}
			continue;
		}
		if (block.type === 'bulletList' || block.type === 'orderedList') {
			for (const item of block.content) {
				collectConditionalAnchors(
					item.content,
					conditional || item.attrs?.rule !== undefined,
					pick,
					into
				);
			}
			continue;
		}
		if (block.type === 'table') {
			for (const row of block.content) {
				for (const cell of row.content) {
					collectConditionalAnchors(
						cell.content,
						conditional || row.attrs?.rule !== undefined,
						pick,
						into
					);
				}
			}
		}
	}
};

/**
 * Signature anchors that sit inside a rule, which the save refuses.
 *
 * A hidden anchor is not a missing stamp — it is a missing PLACEMENT ROW, and the signing path
 * throws `NotFoundException` with no fallback when a Word template has no anchor for a slot.
 * That failure lands at signing prepare, after the deed has already gone out to the recipient,
 * and it names a slot rather than the rule that removed it. Refusing at authoring time is the
 * only point where it is still cheap. Includes anchors nested under a conditional list item or
 * table row, not only ones carrying a rule of their own.
 */
export const conditionalFlowSignatures = (doc: DocumentFlowDoc): FlowSignature['attrs'][] => {
	const found: FlowSignature['attrs'][] = [];
	collectConditionalAnchors(
		doc.content,
		false,
		(block) => (block.type === 'signature' ? block.attrs : null),
		found
	);
	return found;
};

/**
 * Date anchors that sit inside a rule, refused for the same reason: a hidden anchor writes no
 * date placement for that recipient, so their copy is never dated after signing.
 */
export const conditionalFlowDates = (doc: DocumentFlowDoc): FlowDate['attrs'][] => {
	const found: FlowDate['attrs'][] = [];
	collectConditionalAnchors(
		doc.content,
		false,
		(block) => (block.type === 'date' ? block.attrs : null),
		found
	);
	return found;
};

const collectRuledRowsInMergedTables = (blocks: FlowBlock[], into: number[]): void => {
	for (const block of blocks) {
		if (block.type === 'bulletList' || block.type === 'orderedList') {
			block.content.forEach((item) => collectRuledRowsInMergedTables(item.content, into));
			continue;
		}
		if (block.type !== 'table') {
			continue;
		}
		const merged = block.content.some((row) =>
			row.content.some((cell) => (cell.attrs?.rowspan ?? 1) > 1)
		);
		block.content.forEach((row, index) => {
			if (merged && row.attrs?.rule !== undefined) {
				into.push(index);
			}
			row.content.forEach((cell) => collectRuledRowsInMergedTables(cell.content, into));
		});
	}
};

/**
 * Ruled rows in a table with vertically merged cells, which the save refuses.
 *
 * A merge leaves the covered rows without the covered cells, so hiding a row shifts the next
 * row's cells into the wrong columns — and the grid drops whatever falls off the end, signature
 * anchors included.
 */
export const ruledRowsInMergedTables = (doc: DocumentFlowDoc): number[] => {
	const found: number[] = [];
	collectRuledRowsInMergedTables(doc.content, found);
	return found;
};

/** What a marker paragraph reads. Guillemets are already this view's idiom for a field label. */
const markerParagraph = (text: string): FlowBlock => ({
	type: 'paragraph',
	content: [{ type: 'text', text: `«Only if: ${text}»`, marks: [{ type: 'italic' }] }],
});

/** `label` returning null leaves that rule's content unmarked. */
const annotateBlocks = (
	blocks: FlowBlock[],
	label: (rule: string) => string | null
): FlowBlock[] => {
	const out: FlowBlock[] = [];
	let previous: string | undefined;
	for (const block of blocks) {
		const rule = ruleOf(block);
		// One marker per contiguous run, so a three-paragraph rule is labelled once.
		const marker = rule !== undefined && rule !== previous ? label(rule) : null;
		if (marker !== null) {
			out.push(markerParagraph(marker));
		}
		previous = rule;
		if (block.type === 'bulletList' || block.type === 'orderedList') {
			out.push({
				...block,
				content: block.content.map((item) => ({
					...item,
					content: annotateBlocks(item.content, label),
				})),
			});
			continue;
		}
		if (block.type === 'table') {
			out.push({
				...block,
				content: block.content.map((row) => ({
					...row,
					content: row.content.map((cell) => ({
						...cell,
						content: annotateBlocks(cell.content, label),
					})),
				})),
			});
			continue;
		}
		out.push(block);
	}
	return out;
};

const omitRule = <T extends { rule?: string }>({ rule: _rule, ...rest }: T): Omit<T, 'rule'> =>
	rest;

/** The blocks with every rule marking taken off, once their rules have been decided. */
const unmarked = (blocks: FlowBlock[]): FlowBlock[] =>
	blocks.map((block): FlowBlock => {
		switch (block.type) {
			case 'paragraph':
				return block.attrs === undefined
					? block
					: { ...block, attrs: omitRule(block.attrs) };
			case 'bulletList':
			case 'orderedList':
				return {
					...block,
					...(block.attrs === undefined ? {} : { attrs: omitRule(block.attrs) }),
					content: block.content.map((item) => ({
						...item,
						...(item.attrs === undefined ? {} : { attrs: omitRule(item.attrs) }),
						content: unmarked(item.content),
					})),
				};
			case 'table':
				return {
					...block,
					attrs: omitRule(block.attrs),
					content: block.content.map((row) => ({
						...row,
						...(row.attrs === undefined ? {} : { attrs: omitRule(row.attrs) }),
						content: row.content.map((cell) => ({
							...cell,
							content: unmarked(cell.content),
						})),
					})),
				};
			case 'image':
				return { ...block, attrs: omitRule(block.attrs) };
			case 'signature':
				return { ...block, attrs: omitRule(block.attrs) };
			case 'date':
				return block;
		}
	});

/**
 * The preview's copy: hidden content removed, shown content unmarked, and whatever the preview
 * did not decide kept and labelled where it sits.
 *
 * Never throws, unlike {@link resolveFlowRules}: content whose rule is gone is labelled so the
 * author can see where it is. Rules and markings both come off, so neither renderer decides again.
 *
 * No decisions at all is the fields view, which has no recipient and so no honest way to decide
 * a rule — deciding one anyway would teach the author that what they can see is all there is.
 * Every rule's content is kept and labelled instead, which says out loud that this page is
 * nobody's deed. Conditional TABLE ROWS and LIST ITEMS get no marker: a paragraph cannot be
 * inserted between rows, and one between list items would take a number of its own.
 */
export const settleFlowRulesForPreview = (
	doc: DocumentFlowDoc,
	decisions: Readonly<Record<string, FlowRulePreviewDecision>>
): DocumentFlowDoc => {
	const rules = doc.rules ?? {};
	if (Object.keys(rules).length === 0 && flowRuleReferences(doc).size === 0) {
		return doc;
	}
	const decisionOf = (rule: string): FlowRulePreviewDecision | undefined =>
		Object.hasOwn(decisions, rule) ? decisions[rule] : undefined;
	const kept = visibleBlocks(
		doc.content,
		(rule) => rule === undefined || decisionOf(rule) !== 'hidden'
	);
	const content = annotateBlocks(kept, (rule) => {
		const decision = decisionOf(rule);
		if (decision === 'shown') {
			return null;
		}
		return Object.hasOwn(rules, rule) ? rules[rule]?.label ?? rule : 'rule no longer exists';
	});
	return { ...doc, content: unmarked(content), rules: {} };
};
